import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Spinner,
} from 'reactstrap';
import moment from 'moment';
import { useOrganizationStore } from '../../store/organization.store';
import { useNotificationsStore } from '../../store/notifications.store';
import { filterByEnabledSince } from '../../services/notificationFilter';

interface AccessRequest {
  id: string;
  trackId: string;
  trackName: string;
  requesterOrgId: string;
  requesterOrgName: string;
  requesterName: string;
  requesterEmail: string;
  note: string;
  status: 'pending' | 'approved' | 'denied';
  createdAt: number;
}

interface AccessRequestsModalProps {
  firebase: any;
}

const AccessRequestsModal: React.FC<AccessRequestsModalProps> = ({ firebase }) => {
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [denyingId, setDenyingId] = useState<string | null>(null);
  const [denyReason, setDenyReason] = useState('');

  const { currentOrgId } = useOrganizationStore();
  const isOpen = useNotificationsStore(state => state.showAccessRequestsModal);
  const setIsOpen = useNotificationsStore(state => state.setShowAccessRequestsModal);
  const setAccessRequestsCount = useNotificationsStore(state => state.setAccessRequestsCount);

  // Reset auto-open flag when org changes
  useEffect(() => {
    setHasAutoOpened(false);
  }, [currentOrgId]);

  // Real-time listener for pending access requests
  useEffect(() => {
    if (!currentOrgId || !firebase?.subscribeAccessRequestsForOrg) {
      setRequests([]);
      setAccessRequestsCount(0);
      return;
    }

    setLoading(true);
    const unsubscribe = firebase.subscribeAccessRequestsForOrg(
      currentOrgId,
      (reqs: AccessRequest[]) => {
        const enabledSince = useNotificationsStore.getState().inAppEnabledSince;
        const filtered = filterByEnabledSince('accessRequests', reqs, enabledSince);
        setRequests(filtered);
        setAccessRequestsCount(filtered.length);
        setLoading(false);

        // Auto-open on first load if there are pending requests
        if (!hasAutoOpened && reqs.length > 0) {
          setIsOpen(true);
          setHasAutoOpened(true);
        }
      }
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId, firebase]);

  // Close modal when all requests are processed
  useEffect(() => {
    if (isOpen && requests.length === 0 && !loading) {
      setIsOpen(false);
    }
  }, [requests.length, isOpen, loading, setIsOpen]);

  const handleApprove = async (request: AccessRequest) => {
    if (!currentOrgId || !firebase) return;
    setProcessingId(request.id);
    try {
      await firebase.approveAccessRequest(currentOrgId, request.id);
    } catch (err) {
      console.error('Error approving access request:', err);
    }
    setProcessingId(null);
  };

  const handleDenyClick = (request: AccessRequest) => {
    setDenyingId(request.id);
    setDenyReason('');
  };

  const handleDenyCancel = () => {
    setDenyingId(null);
    setDenyReason('');
  };

  const handleDenyConfirm = async (request: AccessRequest) => {
    if (!currentOrgId || !firebase) return;
    setProcessingId(request.id);
    try {
      await firebase.denyAccessRequest(currentOrgId, request.id, denyReason.trim());
    } catch (err) {
      console.error('Error denying access request:', err);
    }
    setProcessingId(null);
    setDenyingId(null);
    setDenyReason('');
  };

  const handleSkip = () => {
    setIsOpen(false);
  };

  if (!isOpen || requests.length === 0) return null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleSkip}
      contentClassName="delete-confirm-modal"
      style={{ color: '#ffffff', maxWidth: '700px' }}
    >
      <ModalHeader
        toggle={handleSkip}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <span style={{ color: '#fff' }}>
          Access Requests ({requests.length})
        </span>
      </ModalHeader>
      <ModalBody
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          color: '#ffffff',
          padding: '1.5rem',
          maxHeight: '500px',
          overflowY: 'auto',
        }}
      >
        <p style={{ marginBottom: '1rem', fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)' }}>
          Broadcasters have requested access to the following spots. Approving will add the spot to their library.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {requests.map(req => (
            <div
              key={req.id}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                padding: '0.75rem 1rem',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#fff', fontWeight: 500, marginBottom: '0.25rem' }}>
                    {req.trackName}
                  </div>
                  <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                    Requested by <strong>{req.requesterOrgName}</strong> ({req.requesterName || req.requesterEmail})
                  </div>
                  <div style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.75rem' }}>
                    {moment(req.createdAt).format('DD/MM/YYYY HH:mm')}
                  </div>
                  {req.note && (
                    <div style={{
                      marginTop: '0.5rem',
                      padding: '0.5rem 0.75rem',
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      borderLeft: '2px solid var(--accent-1-color)',
                      borderRadius: '3px',
                      fontSize: '0.85rem',
                      color: 'rgba(255, 255, 255, 0.75)',
                      fontStyle: 'italic',
                    }}>
                      "{req.note}"
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                  <Button
                    className="btn-danger-custom"
                    size="sm"
                    disabled={processingId === req.id || denyingId === req.id}
                    onClick={() => handleDenyClick(req)}
                    style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                  >
                    <span style={{ position: 'relative', zIndex: 2 }}>Deny</span>
                  </Button>
                  <Button
                    className="btn-success-custom"
                    size="sm"
                    disabled={processingId === req.id || denyingId === req.id}
                    onClick={() => handleApprove(req)}
                    style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                  >
                    <span style={{ position: 'relative', zIndex: 2 }}>
                      {processingId === req.id ? <><Spinner size="sm" className="mr-1" /> ...</> : 'Approve'}
                    </span>
                  </Button>
                </div>
              </div>
              {/* Deny reason input (shown after clicking Deny) */}
              {denyingId === req.id && (
                <div style={{
                  marginTop: '0.75rem',
                  padding: '0.75rem',
                  backgroundColor: 'rgba(255, 80, 80, 0.08)',
                  border: '1px solid rgba(255, 80, 80, 0.2)',
                  borderRadius: '6px',
                }}>
                  <div style={{ marginBottom: '0.5rem', fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                    Provide a reason for denying (optional, but recommended):
                  </div>
                  <Input
                    type="textarea"
                    value={denyReason}
                    onChange={(e) => setDenyReason(e.target.value)}
                    placeholder="e.g. This track is not cleared for third-party use"
                    rows={2}
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: '#fff',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      marginBottom: '0.5rem',
                    }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                    <Button
                      className="btn-info-custom"
                      size="sm"
                      disabled={processingId === req.id}
                      onClick={handleDenyCancel}
                      style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                    >
                      <span style={{ position: 'relative', zIndex: 2 }}>Cancel</span>
                    </Button>
                    <Button
                      className="btn-danger-custom"
                      size="sm"
                      disabled={processingId === req.id}
                      onClick={() => handleDenyConfirm(req)}
                      style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                    >
                      <span style={{ position: 'relative', zIndex: 2 }}>
                        {processingId === req.id ? <><Spinner size="sm" className="mr-1" /> ...</> : 'Confirm Deny'}
                      </span>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </ModalBody>
      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          padding: '1rem 1.5rem',
        }}
      >
        <Button className="btn-info-custom" onClick={handleSkip}>
          <span style={{ position: 'relative', zIndex: 2 }}>Remind Me Later</span>
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AccessRequestsModal;
